import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import useTranslation from 'next-translate/useTranslation'
import { LocaleEnum, useRegisterLeadMutation } from 'generated/apolloComponents'
import styles from './ContactForm.module.scss'

interface IProps {
  onSuccess: () => void
}
const ContactForm = ({ onSuccess }: IProps) => {
  const { t, lang } = useTranslation('pricing')

  const schema = yup.object().shape({
    name: yup.string().required(t('contactNameError')),
    email: yup
      .string()
      .email(t('contactEmailErrorInvalid'))
      .required(t('contactEmailError')),
    company: yup.string().optional(),
    phone: yup.string().optional(),
    position: yup.string().optional(),
    message: yup.string().optional(),
    terms: yup
      .boolean()
      .required(t('contactTermsError'))
      .oneOf([true], t('contactTermsError')),
  })

  const methods = useForm({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company: '',
      position: '',
      message: '',
      terms: false,
    },
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  })
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods

  const [registerLead] = useRegisterLeadMutation({
    onCompleted: () => {
      onSuccess()
      reset()
    },
  })

  const onSubmit = (data: any) => {
    registerLead({
      variables: {
        input: {
          name: data.name,
          email: data.email,
          companyName: data.company,
          phone: data.phone,
          position: data.position,
          message: data.message,
          terms: data.terms,
          language: lang as LocaleEnum,
          source: 'pricing',
        },
      },
    })
  }
  return (
    <form
      id='contact-form'
      onSubmit={handleSubmit(onSubmit)}
      className={styles['contact-form']}
    >
      <div className={`${styles['contact-form__title']} text-4xl font-normal`}>
        {t('contactTitle')}
      </div>
      <div
        className={`${styles['contact-form__form-message']} bg-action-100 text-purple text-md font-thin p-6 rounded-lg`}
      >
        {t('contactIntro')}
      </div>
      <div className={styles['contact-form__name']}>
        <label>
          {t('contactName')}
          <input type='text' {...register('name')} />
          <ErrorMessage
            errors={errors}
            name='name'
            as='p'
            className='text-sm font-thin text-raspberry-500 mt-1'
          />
        </label>
      </div>
      <div className={styles['contact-form__email']}>
        <label>
          {t('contactEmail')}
          <input type='text' {...register('email')} />
          <ErrorMessage
            errors={errors}
            name='email'
            as='p'
            className='text-sm font-thin text-raspberry-500 mt-1'
          />
        </label>
      </div>
      <div className={styles['contact-form__company']}>
        <label>
          {t('contactCompany')}
          <input type='text' {...register('company')} />
          <ErrorMessage
            errors={errors}
            name='company'
            as='p'
            className='text-sm font-thin text-raspberry-500 mt-1'
          />
        </label>
      </div>
      <div className={styles['contact-form__phone']}>
        <label>
          {t('contactPhone')}
          <input type='tel' {...register('phone')} />
          <ErrorMessage
            errors={errors}
            name='phone'
            as='p'
            className='text-sm font-thin text-raspberry-500 mt-1'
          />
        </label>
      </div>
      <div className={styles['contact-form__position']}>
        <label>
          {t('contactPosition')}
          <input
            type='text'
            placeholder={t('contactPositionPlaceholder')}
            {...register('position')}
          />
          <ErrorMessage
            errors={errors}
            name='position'
            as='p'
            className='text-sm font-thin text-raspberry-500 mt-1'
          />
        </label>
      </div>
      <div className={styles['contact-form__message']}>
        <label>
          {t('contactMessage')}
          <input
            type='text'
            placeholder={t('contactMessagePlaceholder')}
            {...register('message')}
          />
          <ErrorMessage
            errors={errors}
            name='message'
            as='p'
            className='text-sm font-thin text-raspberry-500 mt-1'
          />
        </label>
      </div>
      <div className={styles['contact-form__separator']} />

      <div className={styles['contact-form__checkbox']}>
        <label>
          <input type='checkbox' {...register('terms')} />
          {t('contactTerms')}
          <ErrorMessage
            errors={errors}
            name='terms'
            as='p'
            className='text-sm font-thin text-raspberry-500 mt-1'
          />
        </label>
      </div>
      <div className={styles['contact-form__submit']}>
        <button
          type='submit'
          className=' bg-primary text-white w-full px-6 py-4 rounded-full inline-block text-center text-xl font-thin duration-300 hover:bg-primary-hover'
        >
          {t('contactButton')}
        </button>
      </div>
    </form>
  )
}

export default ContactForm
